'use client';

import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { BlockRoot } from '@akademi/site-builder-runtime';
import { useSpatialPorts } from '../data/ports';
import { SignInRequired } from '../data/savedDesigns';
import {
  BID_NOTE_MAX,
  BID_PRICE_MAX,
  COVERAGE_SAVED_EVENT,
  formatJobDate,
  formatPounds,
  isoToday,
  NotAFitter,
  takesQuotes,
  validateBid,
  type BidErrors,
  type FitterAccount,
  type FitterTrade,
  type TradeBidInput,
  type TradeJob,
  type TradeJobs,
} from '../data/tradeJobs';
import { Icon } from '../shared/Icon';
import { childLevel, headingLevel, SkLink, text, type HeadingLevel, type Localized } from '../shared/primitives';
import { NotOpen } from '../shared/NotOpen';
import { SkBaseStyles, SkStyles } from '../styles/SkStyles';
import { JOB_BOARD_CSS, TRADE_SHARED_CSS } from '../trade/tradeCss';
import {
  anchorId,
  FITTER_APPLICATION_HREF,
  messageOf,
  sendToSignIn,
  tradeOptions,
  useSignInHref,
  type TradeOptionProp,
} from '../trade/tradeShared';
import { homeSurfaceClass, HomeSurfaceStyles, type HomeSurface } from './homeShared';

/**
 * The fitter's job board (site mockup `trade.html`, "Live near you"): the jobs
 * the shop opened in the fitter's areas and trades, plus every job they quoted
 * on, filtered by trade and sorted newest or soonest.
 *
 * <h3>Quotes</h3>
 * "Quote for this job" opens a quote form in a native `<dialog>`. Quotes go to
 * the shop, not the customer. Sending again replaces the earlier quote; a quote
 * can be withdrawn while the job is open. Each job shows the fitter's quote and
 * where it stands: sent, withdrawn, accepted or not chosen.
 *
 * <h3>An accepted quote</h3>
 * The job then shows the full site postcode, and says the shop will contact the
 * fitter to arrange the survey. Customer details stay with the shop.
 *
 * <h3>Data</h3>
 * The `tradeJobs` port; the server decides which jobs a fitter sees. The board
 * reloads when a coverage block on the page saves (`COVERAGE_SAVED_EVENT`).
 * Nobody signed in: sent to sign in and back. Signed in but not a fitter: a
 * link to the fitter application.
 *
 * <h3>Privacy</h3>
 * An open job shows its outward code and area only. The page is `noindex`.
 */
export interface SpatialKitchenJobBoardProps {
  eyebrow?: Localized;
  title?: Localized;
  intro?: Localized;
  /** Trades offered in the filter, by backend id (`KITCHEN_FITTING`). Empty uses the default list. */
  trades?: TradeOptionProp[];
  /** Where fitters set their postcodes, linked from the empty state, e.g. `#coverage`. */
  coverageHref?: string;
  /** Show a sign-in link instead of the quote button. */
  requireSignIn?: boolean;
  signInHref?: string;
  /** The fitter application page, linked for a signed-in visitor who is not a fitter. */
  applyHref?: string;
  surface?: HomeSurface;
  headingLevel?: HeadingLevel;
  /** The section's id, for links such as `#coverage` or `#jobs`. */
  anchorId?: string;
}

export type JobSort = 'newest' | 'soonest';

/** Jobs the fitter can act on or has won come first; the rest (closed, cancelled, awarded to someone else) after. */
function rank(job: TradeJob): number {
  return job.status === 'open' || job.myBid?.status === 'accepted' ? 0 : 1;
}

/** Whether a job is shown muted: it is over for this fitter. */
export function isMuted(job: TradeJob): boolean {
  if (job.myBid?.status === 'accepted') return false;
  return job.status !== 'open' || job.myBid?.status === 'declined';
}

/** The jobs to list: open (and won) before the rest, then by the chosen order; `trade` narrows to jobs needing it. */
export function boardJobs(jobs: readonly TradeJob[], options: { trade?: FitterTrade | ''; sort: JobSort }): TradeJob[] {
  const matching = options.trade ? jobs.filter((job) => job.trades.includes(options.trade as FitterTrade)) : [...jobs];
  const order = (a: TradeJob, b: TradeJob) =>
    options.sort === 'soonest' ? a.readyFrom.localeCompare(b.readyFrom) : b.postedAt.localeCompare(a.postedAt);
  return matching.sort((a, b) => rank(a) - rank(b) || order(a, b));
}

type Load =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'signIn' }
  | { kind: 'notFitter' }
  | { kind: 'ready'; account: FitterAccount; jobs: TradeJob[] };

type Status = { kind: 'ok' | 'error'; message: string } | null;

export function SpatialKitchenJobBoard(props: SpatialKitchenJobBoardProps) {
  const { tradeJobs } = useSpatialPorts();
  if (!tradeJobs) {
    return (
      <NotOpen
        level={props.headingLevel ?? 'h2'}
        title={text(props.title, 'Fitting jobs')}
        message="The fitting-job board is not open yet. When the shop has fitting jobs, they will appear here for fitters in their area."
        contactHref={props.applyHref?.trim() || FITTER_APPLICATION_HREF}
      />
    );
  }
  return <JobBoard {...props} tradeJobs={tradeJobs} />;
}

function JobBoard(props: SpatialKitchenJobBoardProps & { tradeJobs: TradeJobs }) {
  const id = useId();
  const { tradeJobs } = props;
  const level = headingLevel(props.headingLevel, 'h2');
  const Title = level;
  const JobTitle = childLevel(level);
  const trades = tradeOptions(props.trades);
  const signInLink = useSignInHref(props.signInHref);
  const applyHref = props.applyHref?.trim() || FITTER_APPLICATION_HREF;
  const dialogRef = useRef<HTMLDialogElement>(null);

  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [trade, setTrade] = useState<string>('');
  const [sort, setSort] = useState<JobSort>('newest');
  const [quoting, setQuoting] = useState<TradeJob | null>(null);
  const [status, setStatus] = useState<Status>(null);
  const [withdrawing, setWithdrawing] = useState<string | null>(null);

  const signInHref = props.signInHref;
  const fetchJobs = useCallback(() => {
    let current = true;
    setLoad((previous) => (previous.kind === 'ready' ? previous : { kind: 'loading' }));
    tradeJobs
      .getAccount()
      .then(async (account) => ({
        account,
        // No fitter account yet (an application under review or refused): there are no jobs to ask for.
        jobs: account.state === 'APPLIED' || account.state === 'REJECTED' ? [] : await tradeJobs.jobs(),
      }))
      .then(({ account, jobs }) => {
        if (current) setLoad({ kind: 'ready', account, jobs });
      })
      .catch((error: unknown) => {
        if (!current) return;
        if (error instanceof SignInRequired) {
          setLoad({ kind: 'signIn' });
          sendToSignIn(signInHref);
        } else if (error instanceof NotAFitter) {
          setLoad({ kind: 'notFitter' });
        } else {
          setLoad({ kind: 'error', message: messageOf(error, 'The jobs could not be loaded.') });
        }
      });
    return () => {
      current = false;
    };
  }, [tradeJobs, signInHref]);

  useEffect(() => {
    let cancel = fetchJobs();
    const reload = () => {
      cancel();
      cancel = fetchJobs();
    };
    window.addEventListener(COVERAGE_SAVED_EVENT, reload);
    return () => {
      cancel();
      window.removeEventListener(COVERAGE_SAVED_EVENT, reload);
    };
  }, [fetchJobs]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || !quoting || dialog.open) return;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
  }, [quoting]);

  function closeDialog() {
    const dialog = dialogRef.current;
    if (dialog?.open) {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    }
    setQuoting(null);
  }

  /** Puts the server's answer for one job in place of the one listed. */
  function replaceJob(updated: TradeJob) {
    setLoad((previous) =>
      previous.kind === 'ready' ? { ...previous, jobs: previous.jobs.map((job) => (job.id === updated.id ? updated : job)) } : previous,
    );
  }

  async function sendQuote(job: TradeJob, bid: TradeBidInput) {
    const again = job.myBid?.status === 'sent';
    try {
      replaceJob(await tradeJobs.placeBid(job.id, bid));
    } catch (error) {
      if (error instanceof SignInRequired) {
        sendToSignIn(signInHref);
        return;
      }
      throw error;
    }
    closeDialog();
    setStatus({ kind: 'ok', message: again ? `Your quote for "${job.title}" was updated.` : `Your quote for "${job.title}" was sent to the shop.` });
  }

  async function withdraw(job: TradeJob) {
    setWithdrawing(job.id);
    setStatus(null);
    try {
      replaceJob(await tradeJobs.withdrawBid(job.id));
      setStatus({ kind: 'ok', message: `Your quote for "${job.title}" was withdrawn.` });
    } catch (error) {
      if (error instanceof SignInRequired) {
        sendToSignIn(signInHref);
        return;
      }
      setStatus({ kind: 'error', message: messageOf(error, 'Your quote was not withdrawn. Please try again.') });
    } finally {
      setWithdrawing(null);
    }
  }

  const ready = load.kind === 'ready' ? load : null;
  const list = ready ? boardJobs(ready.jobs, { trade, sort }) : [];
  const openCount = list.filter((job) => job.status === 'open').length;
  const intro = text(props.intro);
  const hasFitterAccount = ready && ready.account.state !== 'APPLIED' && ready.account.state !== 'REJECTED';
  const now = new Date();

  function action(job: TradeJob, account: FitterAccount) {
    const bid = job.myBid;
    const quotable = takesQuotes(job, now) && account.canQuote;
    if (bid?.status === 'accepted') return null;
    if (bid?.status === 'declined') {
      return <span className="sk-trade__hint">{`Your quote of ${formatPounds(bid.price)} was not chosen.`}</span>;
    }
    const mine =
      bid?.status === 'sent' ? (
        <span className="sk-jobs__sent">
          <Icon name="check" size={18} />
          {`Your quote: ${formatPounds(bid.price)}, from ${formatJobDate(bid.availableFrom)}`}
        </span>
      ) : bid?.status === 'withdrawn' ? (
        <span className="sk-jobs__mine">You withdrew your quote.</span>
      ) : null;
    if (!quotable) {
      const why = !takesQuotes(job, now)
        ? bid?.status === 'sent'
          ? 'Quotes have closed. The shop will let you know its decision.'
          : 'This job is no longer taking quotes.'
        : 'You cannot quote until your fitter account is in order.';
      return (
        <>
          {mine}
          <span className="sk-trade__hint">{why}</span>
        </>
      );
    }
    if (props.requireSignIn) {
      return (
        <>
          {mine}
          <SkLink href={signInLink} className="sk-btn sk-btn--secondary sk-btn--sm">
            Sign in to quote
          </SkLink>
        </>
      );
    }
    const label = bid?.status === 'sent' ? 'Change your quote' : bid?.status === 'withdrawn' ? 'Quote again' : 'Quote for this job';
    return (
      <>
        {mine}
        <button
          type="button"
          className={`sk-btn ${bid?.status === 'sent' ? 'sk-btn--secondary' : 'sk-btn--primary'} sk-btn--sm`}
          aria-label={`${label}: ${job.title}, ${job.outward}`}
          onClick={() => {
            setStatus(null);
            setQuoting(job);
          }}
        >
          {label}
        </button>
        {bid?.status === 'sent' && (
          <button
            type="button"
            className="sk-btn sk-btn--secondary sk-btn--sm"
            aria-label={`Withdraw your quote: ${job.title}, ${job.outward}`}
            disabled={withdrawing === job.id}
            onClick={() => void withdraw(job)}
          >
            {withdrawing === job.id ? 'Withdrawing…' : 'Withdraw'}
          </button>
        )}
      </>
    );
  }

  function statusTag(job: TradeJob): string | null {
    if (job.myBid?.status === 'accepted') return 'Your quote accepted';
    if (job.status === 'awarded') return 'Awarded';
    if (job.status === 'closed') return 'Closed';
    if (job.status === 'cancelled') return 'Cancelled';
    return null;
  }

  return (
    <BlockRoot as="section" id={anchorId(props.anchorId)} className={`sk sk-trade sk-jobs ${homeSurfaceClass(props.surface, 'white')}`} aria-labelledby={`${id}-title`}>
      <SkBaseStyles />
      <HomeSurfaceStyles />
      <SkStyles id="trade" css={TRADE_SHARED_CSS} />
      <SkStyles id="job-board" css={JOB_BOARD_CSS} />
      <div className="sk-container">
        <div className="sk-jobs__head">
          <div>
            <p className="sk-eyebrow">{text(props.eyebrow, 'Fitting jobs')}</p>
            <Title id={`${id}-title`} className="sk-heading sk-h2">
              {text(props.title, 'Jobs in your area')}
            </Title>
            {intro && <p className="sk-lead">{intro}</p>}
          </div>
        </div>

        {(load.kind === 'loading' || hasFitterAccount) && (
          <div className="sk-jobs__tools">
            <div className="sk-trade__field">
              <label className="sk-trade__label" htmlFor={`${id}-trade`}>
                Trade
              </label>
              <select id={`${id}-trade`} className="sk-trade__input" value={trade} onChange={(e) => setTrade(e.target.value)}>
                <option value="">All trades</option>
                {trades.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="sk-trade__field">
              <label className="sk-trade__label" htmlFor={`${id}-sort`}>
                Sort by
              </label>
              <select id={`${id}-sort`} className="sk-trade__input" value={sort} onChange={(e) => setSort(e.target.value === 'soonest' ? 'soonest' : 'newest')}>
                <option value="newest">Newest first</option>
                <option value="soonest">Soonest start first</option>
              </select>
            </div>
          </div>
        )}

        {status && (
          <p className={`sk-trade__status sk-trade__status--${status.kind}`} role={status.kind === 'error' ? 'alert' : 'status'}>
            {status.message}
          </p>
        )}

        <div>
          {load.kind === 'loading' && <p className="sk-jobs__state sk-body" role="status">Loading jobs…</p>}
          {load.kind === 'error' && (
            <div role="alert">
              <p className="sk-trade__status sk-trade__status--error">{load.message}</p>
              <div className="sk-trade__actions">
                <button type="button" className="sk-trade__retry" onClick={() => fetchJobs()}>
                  Try again
                </button>
              </div>
            </div>
          )}
          {load.kind === 'signIn' && (
            <div className="sk-trade__panel">
              <p className="sk-jobs__state sk-body">
                Sign in to see fitting jobs. <SkLink href={signInLink}>Sign in</SkLink>
              </p>
            </div>
          )}
          {load.kind === 'notFitter' && (
            <div className="sk-trade__panel">
              <p className="sk-jobs__state sk-body">
                Fitting jobs are for the shop&rsquo;s approved fitters.{' '}
                <SkLink href={applyHref}>Apply to become a fitter</SkLink>
              </p>
            </div>
          )}
          {ready && !hasFitterAccount && (
            <div className="sk-trade__panel">
              <p className="sk-jobs__state sk-body">
                {ready.account.state === 'APPLIED'
                  ? 'Jobs appear here once the shop has approved your fitter application.'
                  : 'Your fitter application was not approved, so there are no jobs to show.'}
              </p>
            </div>
          )}
          {ready && hasFitterAccount && ready.account.postcodes.length === 0 && ready.jobs.length === 0 && (
            <div className="sk-trade__panel">
              <p className="sk-jobs__state sk-body">
                Add the postcodes you cover to see jobs there.{' '}
                {props.coverageHref?.trim() && <SkLink href={props.coverageHref.trim()}>Choose where you work</SkLink>}
              </p>
            </div>
          )}
          {ready && hasFitterAccount && (ready.account.postcodes.length > 0 || ready.jobs.length > 0) && (
            <>
              {!ready.account.canQuote && (
                <p className="sk-trade__notice" role="status">
                  You cannot send quotes until your fitter account is in order.{' '}
                  {props.coverageHref?.trim() && <SkLink href={props.coverageHref.trim()}>See what is needed</SkLink>}
                </p>
              )}
              <p className="sk-jobs__where" role="status">
                {`${openCount} open job${openCount === 1 ? '' : 's'}${ready.account.postcodes.length ? ` in ${ready.account.postcodes.join(', ')}` : ''}`}
                {trade ? ` for ${trades.find((t) => t.id === trade)?.label ?? trade}` : ''}
              </p>
              {list.length === 0 ? (
                <div className="sk-trade__panel">
                  <p className="sk-jobs__state sk-body">
                    {trade
                      ? 'No jobs for that trade in your postcodes right now. Try all trades.'
                      : 'No jobs in your postcodes right now. Adding more postcodes shows more work.'}
                  </p>
                </div>
              ) : (
                <ul className="sk-jobs__list">
                  {list.map((job) => {
                    const tag = statusTag(job);
                    const won = job.myBid?.status === 'accepted' ? job.myBid : null;
                    return (
                      <li key={job.id} className="sk-jobs__job" data-muted={isMuted(job) ? '' : undefined}>
                        <div>
                          <JobTitle>{job.title}</JobTitle>
                          <p className="sk-jobs__where-line">
                            {[job.reference, job.outward, job.areaLabel, `Ready from ${formatJobDate(job.readyFrom)}`].filter(Boolean).join(' · ')}
                          </p>
                          {job.kitchenSummary && <p className="sk-jobs__kitchen">{job.kitchenSummary}</p>}
                        </div>
                        <div className="sk-jobs__side">
                          <b>{`${job.bids} quote${job.bids === 1 ? '' : 's'} so far`}</b>
                          <small>{`Posted ${formatJobDate(job.postedAt)}`}</small>
                          {job.closesAt && job.status === 'open' && <small>{`Quotes close ${formatJobDate(job.closesAt)}`}</small>}
                        </div>
                        <ul className="sk-jobs__tags" aria-label="About this job">
                          {tag && <li className="sk-jobs__tag--closed">{tag}</li>}
                          {job.scope.map((line, i) => (
                            <li key={i}>{line}</li>
                          ))}
                        </ul>
                        {job.budgetGuide && <p className="sk-jobs__budget">{`Budget guide: ${job.budgetGuide}`}</p>}
                        {job.packNotes && <p className="sk-jobs__pack">{`Pack notes: ${job.packNotes}`}</p>}
                        {won && (
                          <div className="sk-jobs__won" role="note">
                            <strong>{`Your quote of ${formatPounds(won.price)} was accepted.`}</strong>
                            {job.sitePostcode && <span>{`Site postcode: ${job.sitePostcode}`}</span>}
                            <span>The shop will contact you to arrange the survey.</span>
                          </div>
                        )}
                        <div className="sk-jobs__cta">{action(job, ready.account)}</div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </div>
      </div>

      <dialog
        ref={dialogRef}
        className="sk-jobs__dialog"
        aria-labelledby={`${id}-quote-title`}
        onClose={() => setQuoting(null)}
      >
        {quoting && (
          <BidForm
            key={quoting.id}
            job={quoting}
            titleId={`${id}-quote-title`}
            initial={quoting.myBid?.status === 'sent' ? quoting.myBid : undefined}
            onSubmit={(bid) => sendQuote(quoting, bid)}
            onCancel={closeDialog}
          />
        )}
      </dialog>
    </BlockRoot>
  );
}

export interface BidFormProps {
  job: Pick<TradeJob, 'id' | 'title' | 'outward' | 'areaLabel' | 'readyFrom'>;
  titleId: string;
  /** The fitter's earlier quote, to change; sending replaces it. */
  initial?: TradeBidInput;
  /** Resolves when the quote is sent; a rejection's message is shown in the form. */
  onSubmit(bid: TradeBidInput): Promise<void>;
  onCancel(): void;
  /** `YYYY-MM-DD`; the earliest start date allowed. Defaults to today. */
  today?: string;
}

/** The quote form inside the dialog: price, first available date and a note. */
export function BidForm({ job, titleId, initial, onSubmit, onCancel, today = isoToday() }: BidFormProps) {
  const id = useId();
  const [price, setPrice] = useState(initial ? String(initial.price) : '');
  const [availableFrom, setAvailableFrom] = useState(initial && initial.availableFrom >= today ? initial.availableFrom : '');
  const [note, setNote] = useState(initial?.note ?? '');
  const [errors, setErrors] = useState<BidErrors>({});
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const found = validateBid({ price, availableFrom, note }, today);
    setErrors(found);
    setFailure(null);
    if (Object.keys(found).length) {
      const first = (['price', 'availableFrom', 'note'] as const).find((key) => found[key]);
      if (first) document.getElementById(`${id}-${first}`)?.focus();
      return;
    }
    setBusy(true);
    try {
      await onSubmit({ price: Number(price.trim().replace(/^£/, '').replace(/,/g, '')), availableFrom, note: note.trim() });
    } catch (error) {
      setFailure(messageOf(error, 'Your quote was not sent. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  const field = (key: keyof TradeBidInput, hint?: string) => ({
    id: `${id}-${key}`,
    'aria-invalid': errors[key] ? true : undefined,
    'aria-describedby': [hint, errors[key] ? `${id}-${key}-error` : ''].filter(Boolean).join(' ') || undefined,
  });

  return (
    <form className="sk-jobs__form" noValidate onSubmit={submit}>
      <div>
        <p className="sk-eyebrow">{`${job.outward} · ${job.areaLabel}`}</p>
        <h2 id={titleId} className="sk-heading">
          {`Quote for: ${job.title}`}
        </h2>
        <p className="sk-trade__hint">{`Ready from ${formatJobDate(job.readyFrom)}`}</p>
        <p className="sk-trade__hint">
          {initial ? 'Sending again replaces your earlier quote.' : 'Your quote goes to the shop, which arranges the work with the customer.'}
        </p>
      </div>
      <div className="sk-jobs__formgrid">
        <div className="sk-trade__field">
          <label className="sk-trade__label" htmlFor={`${id}-price`}>
            Your price for the fitting (£)
          </label>
          <div className="sk-jobs__money">
            <span aria-hidden="true">£</span>
            <input
              {...field('price', `${id}-price-hint`)}
              className="sk-trade__input"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              required
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
          </div>
          <p className="sk-trade__hint" id={`${id}-price-hint`}>
            {`Including VAT, up to ${formatPounds(BID_PRICE_MAX)}.`}
          </p>
          {errors.price && (
            <p className="sk-trade__error" id={`${id}-price-error`}>
              {errors.price}
            </p>
          )}
        </div>
        <div className="sk-trade__field">
          <label className="sk-trade__label" htmlFor={`${id}-availableFrom`}>
            Available from
          </label>
          <input
            {...field('availableFrom')}
            className="sk-trade__input"
            type="date"
            min={today}
            required
            value={availableFrom}
            onChange={(e) => setAvailableFrom(e.target.value)}
          />
          {errors.availableFrom && (
            <p className="sk-trade__error" id={`${id}-availableFrom-error`}>
              {errors.availableFrom}
            </p>
          )}
        </div>
      </div>
      <div className="sk-trade__field">
        <label className="sk-trade__label" htmlFor={`${id}-note`}>
          Note for the shop (optional)
        </label>
        <textarea
          {...field('note', `${id}-note-count`)}
          className="sk-trade__input sk-jobs__textarea"
          maxLength={BID_NOTE_MAX}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <p className="sk-jobs__count" id={`${id}-note-count`}>
          {`${note.length} of ${BID_NOTE_MAX} characters`}
        </p>
        {errors.note && (
          <p className="sk-trade__error" id={`${id}-note-error`}>
            {errors.note}
          </p>
        )}
      </div>
      {failure && (
        <p className="sk-trade__status sk-trade__status--error" role="alert">
          {failure}
        </p>
      )}
      <div className="sk-jobs__formactions">
        <button type="button" className="sk-btn sk-btn--secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="submit" className="sk-btn sk-btn--primary" disabled={busy}>
          {busy ? 'Sending…' : initial ? 'Send new quote' : 'Send quote'}
        </button>
      </div>
    </form>
  );
}
